import { FC, MouseEvent, useCallback, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import TableCell, { TableCellProps } from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { useSortableField } from 'components/TableSorting';

import { useHoverable } from 'lib';
import TableHeadCellSort from 'components/TableHeadCellSort';

type Props = TableCellProps & {
  name: string;
  center?: boolean;
};

const TableHeadCell: FC<Props> = props => {
  const { name, children, sx = [], center, ...rest } = props;
  const { hovering, ...hoverable } = useHoverable();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [dir, setSort] = useSortableField(name);

  const handleClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const closeMenu = useCallback(() => {
    setAnchorEl(null);
    hoverable.onMouseLeave();
  }, [hoverable]);

  const handleSortAsc = useCallback(() => {
    setSort('ASC');
    closeMenu();
  }, [closeMenu, setSort]);

  const handleSortDesc = useCallback(() => {
    setSort('DESC');
    closeMenu();
  }, [closeMenu, setSort]);

  const handleNoSort = useCallback(() => {
    setSort(null);
    closeMenu();
  }, [closeMenu, setSort]);

  return (
    <TableCell
      sx={[
        {
          position: 'relative',
        },
        center && {
          textAlign: 'center',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...hoverable}
      {...rest}>
      {children}
      {hovering ? (
        <>
          <IconButton
            size="small"
            sx={{ position: 'absolute', right: 0 }}
            onClick={handleClick}>
            <MoreVertIcon fontSize="inherit" />
          </IconButton>

          <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
            {dir === 'ASC' ? (
              <MenuItem onClick={handleNoSort}>
                <ListItemIcon>
                  <CloseIcon fontSize="inherit" />
                </ListItemIcon>

                <ListItemText>Nerikiuoti</ListItemText>
              </MenuItem>
            ) : (
              <MenuItem onClick={handleSortAsc}>
                <ListItemIcon>
                  <ArrowUpwardIcon fontSize="inherit" />
                </ListItemIcon>

                <ListItemText>Rikiuoti didėjimo tvarka</ListItemText>
              </MenuItem>
            )}

            {dir === 'DESC' ? (
              <MenuItem onClick={handleNoSort}>
                <ListItemIcon>
                  <CloseIcon fontSize="inherit" />
                </ListItemIcon>

                <ListItemText>Nerikiuoti</ListItemText>
              </MenuItem>
            ) : (
              <MenuItem onClick={handleSortDesc}>
                <ListItemIcon>
                  <ArrowDownwardIcon fontSize="inherit" />
                </ListItemIcon>

                <ListItemText>Rikiuoti mažėjimo tvarka</ListItemText>
              </MenuItem>
            )}
          </Menu>
        </>
      ) : (
        <TableHeadCellSort sort={dir} />
      )}
    </TableCell>
  );
};

export default TableHeadCell;
